import qahoy from "views_shared/app/javascript/scripts/qahoy";
import qapi from "views_shared/app/javascript/api";
import { serializeModel } from "../../pr_profiles/edit/employment_modal_body/serialization";

// Context-fed action hook for an expert's employment — the sibling of use_expert_update_api, and the
// expert-side counterpart of the PR page's use_pr_employments_api. The provider owns the `companyJobs`
// collection; this performs writes against it (optimistic update + snapshot rollback), routes failures to
// the shared generalError (never asyncError, which would tear the page down), and reconciles by reloading.
// It holds no state of its own.
//
// The rows are stored in the same camelCase shape the PR page's serializer produces (the provider
// deserializes with it), so writes serialize with that same serializer. The companyJobs mutation client
// throws on a non-2xx (throwOnApiError), so these try/catch blocks are load-bearing.
const useExpertEmploymentsApi = ({
  expertId,
  companyJobs,
  setCompanyJobs,
  silentReload,
  setGeneralError,
  setIsUpdating,
  isUpdating,
}) => {
  // Re-sync page state from whatever the server actually committed after a failed write — the local
  // rollback restores the pre-write collection, which is only right if the write truly did nothing.
  // Nonfatal so a refresh that also fails cannot escalate a recoverable, already-surfaced save error
  // into the page error boundary; the rejection is swallowed for the same reason.
  const reconcile = async () => {
    try {
      await silentReload({ nonfatal: true });
    } catch {
      // Deliberately empty: the save error is already on generalError.
    }
  };

  const addEmployment = async (employment) => {
    if (isUpdating) return; // guard overlapping mutations
    setGeneralError(null);
    setIsUpdating(true);
    try {
      qahoy.track("Experts#addJob");
      const { id: _removed, ...serialized } = serializeModel(employment);
      // A reload (not a manual optimistic insert) is simplest here — the new row only has a server id
      // after the create, and the reload re-orders it correctly via the provider fetch.
      await qapi.companyJobs.createExpertJob(expertId, serialized);
      await silentReload();
    } catch (e) {
      setGeneralError(e);
      await reconcile();
    } finally {
      setIsUpdating(false);
    }
  };

  const removeEmployment = async (id) => {
    if (isUpdating) return;
    setGeneralError(null);
    const snapshot = companyJobs;

    setCompanyJobs((current) => current.filter((job) => job.id !== id)); // optimistic

    setIsUpdating(true);
    try {
      qahoy.track("Experts#removeJob");
      await qapi.companyJobs.deleteExpertJobById(expertId, id);
      await silentReload();
    } catch (e) {
      setCompanyJobs(snapshot); // roll back
      setGeneralError(e);
      await reconcile();
    } finally {
      setIsUpdating(false);
    }
  };

  const updateEmployment = async (id, employment) => {
    if (isUpdating) return;
    setGeneralError(null);
    const snapshot = companyJobs;

    setCompanyJobs((current) => current.map((job) => (job.id === id ? { ...job, ...employment } : job))); // optimistic

    setIsUpdating(true);
    try {
      qahoy.track("Experts#updateJob");
      const { id: _id, ...attributes } = employment;
      await qapi.companyJobs.updateExpertJobById(expertId, id, serializeModel(attributes));
      await silentReload();
    } catch (e) {
      setCompanyJobs(snapshot); // roll back
      setGeneralError(e);
      await reconcile();
    } finally {
      setIsUpdating(false);
    }
  };

  // person_featured is flip-only (never writable via update), so this goes through the dedicated
  // feature/unfeature endpoints. Featuring clears any sibling star server-side (one featured job per
  // person), so the optimistic update mirrors that; the reload then re-orders the collection and
  // refreshes the header title, which derives from the featured job.
  const setFeatured = async (id, featured) => {
    if (isUpdating) return;
    setGeneralError(null);
    const snapshot = companyJobs;

    setCompanyJobs((current) =>
      current.map((job) => {
        if (job.id === id) return { ...job, featured };
        return featured ? { ...job, featured: false } : job;
      }),
    ); // optimistic

    setIsUpdating(true);
    try {
      qahoy.track(featured ? "Experts#featureJob" : "Experts#unfeatureJob");
      await qapi.companyJobs.setExpertJobFeaturedById(expertId, id, featured);
      await silentReload();
    } catch (e) {
      setCompanyJobs(snapshot); // roll back
      setGeneralError(e);
      await reconcile();
    } finally {
      setIsUpdating(false);
    }
  };

  return { addEmployment, removeEmployment, setFeatured, updateEmployment };
};

export default useExpertEmploymentsApi;
